import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { mediaAPI } from '../../services/api';
import { Button } from '../ui';
import { handleApiError, getImageUrl, showToast } from '../../utils/helpers';
import { X } from 'lucide-react';

/**
 * Modal to pick a media item from client's library (e.g. for SEO Design 1/2)
 * Props: clientId, clientName, isOpen, onClose, onSelect(mediaItem), imageOnly
 */
const MediaLibraryPickerModal = ({
  clientId,
  clientName,
  isOpen,
  onClose,
  onSelect,
  imageOnly = true,
}) => {
  const { isRTL } = useLanguage();
  const [media, setMedia] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderPath, setFolderPath] = useState([]);

  const fetchData = async () => {
    if (!clientId || !isOpen) return;
    try {
      setLoading(true);
      const [mediaRes, foldersRes] = await Promise.all([
        mediaAPI.getAll({
          client_id: clientId,
          folder_id: currentFolderId || 'root',
          page: 1,
          limit: 100,
        }),
        mediaAPI.getAllFolders({
          client_id: clientId,
          parent_id: currentFolderId || 'root',
        }),
      ]);
      let list = mediaRes.data.media || [];
      if (imageOnly) {
        list = list.filter((m) => m.file_type === 'image' || (m.mime_type && m.mime_type.startsWith('image/')));
      }
      setMedia(list);
      setFolders(foldersRes.data.folders || []);
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [clientId, currentFolderId, isOpen]);

  const handleFolderClick = (folder) => {
    setCurrentFolderId(folder.id);
    setFolderPath((prev) => [...prev, folder]);
  };

  const handleBreadcrumbClick = (index) => {
    if (index === -1) {
      setCurrentFolderId(null);
      setFolderPath([]);
    } else {
      const newPath = folderPath.slice(0, index + 1);
      setCurrentFolderId(newPath[newPath.length - 1].id);
      setFolderPath(newPath);
    }
  };

  const handleSelect = (m) => {
    onSelect?.(m);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">
            {isRTL ? `اختر من مكتبة الوسائط — ${clientName || 'العميل'}` : `Select from Media Library — ${clientName || 'Client'}`}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 border-b border-slate-100">
          <button onClick={() => handleBreadcrumbClick(-1)} className="hover:text-sky-600 font-medium">
            {isRTL ? 'الرئيسية' : 'Home'}
          </button>
          {folderPath.map((f, i) => (
            <React.Fragment key={f.id}>
              <span>/</span>
              <button onClick={() => handleBreadcrumbClick(i)} className="hover:text-sky-600 font-medium truncate max-w-[100px]">
                {f.name}
              </button>
            </React.Fragment>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="py-12 text-center text-slate-500">{isRTL ? 'جاري التحميل...' : 'Loading...'}</div>
          ) : (
            <div className="space-y-4">
              {folders.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-slate-500 uppercase mb-2">{isRTL ? 'المجلدات' : 'Folders'}</h4>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                    {folders.map((folder) => (
                      <button
                        key={folder.id}
                        type="button"
                        onClick={() => handleFolderClick(folder)}
                        className="flex flex-col items-center p-3 rounded-lg border-2 border-slate-200 hover:border-amber-400 hover:bg-amber-50/50 transition-all text-left"
                      >
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-sky-50 to-emerald-50 flex items-center justify-center text-2xl mb-2">
                          📁
                        </div>
                        <span className="text-xs font-medium text-slate-800 truncate w-full">{folder.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <h4 className="text-xs font-medium text-slate-500 uppercase mb-2">
                  {isRTL ? 'الملفات — اضغط للاختيار' : 'Files — click to select'}
                </h4>
                {media.length === 0 && folders.length === 0 ? (
                  <div className="py-12 text-center text-slate-500 text-sm border-2 border-dashed border-slate-200 rounded-xl">
                    {isRTL ? 'لا توجد ملفات أو مجلدات في المكتبة.' : 'No files or folders in the library.'}
                  </div>
                ) : media.length === 0 ? (
                  <div className="py-8 text-center text-slate-500 text-sm">{isRTL ? 'لا توجد ملفات في هذا المجلد' : 'No files in this folder'}</div>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-2">
                    {media.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => handleSelect(m)}
                        className="flex flex-col rounded-lg border-2 border-slate-200 hover:border-amber-400 hover:ring-2 hover:ring-amber-200 overflow-hidden transition-all group"
                      >
                        <div className="aspect-square bg-slate-100 flex items-center justify-center overflow-hidden">
                          {m.file_type === 'video' ? (
                            <video src={getImageUrl(m.file_url)} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                          ) : (
                            <img src={getImageUrl(m.file_url)} alt={m.file_name} className="w-full h-full object-cover" />
                          )}
                        </div>
                        <div className="p-1.5 bg-white">
                          <p className="text-[10px] text-slate-600 truncate" title={m.file_name}>{m.file_name}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 flex justify-between">
          <Button
            variant="ghost"
            onClick={() => { onSelect?.(null); onClose(); }}
            className="text-slate-500 hover:text-rose-600"
          >
            {isRTL ? 'إزالة' : 'Clear'}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {isRTL ? 'إلغاء' : 'Cancel'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MediaLibraryPickerModal;
